import React, { useState } from "react";
import { CreditCard, Wallet, Loader2 } from "lucide-react";
import { Button } from "./Button";
import { Card } from "./card";
import PaymentMethodSelector from "../payment/PaymentMethodSelector";
import { getDynamicCurrencySymbol } from "../../lib/currencyUtils";
import { useTranslation } from "react-i18next";
import { getPlatformFeeForContract } from "../../utils/categoryFormSettingsUtils";
import { PLATFORM_CONFIG } from "../../config/platformConfig";
import { CategoryFormSettings } from "../../modules/shared/model/CategoryFormSettingsInterfaces";
import { Elements } from "@stripe/react-stripe-js";
import { loadStripe } from "@stripe/stripe-js";

// ✅ FIX: Initialize Stripe
const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY || "");

interface Props {
	milestone: any;
	onPayment: (paymentData: any) => void;
	loading?: boolean;
	availableBalance?: number;
	checkingBalance?: boolean;
	useExistingFunds?: boolean;
	setUseExistingFunds?: (value: boolean) => void;
	categoryFormSetting?: CategoryFormSettings | null;
}

// ✅ FIX: Inner component that uses Stripe hooks
const EnhancedPaymentFormContent: React.FC<Props> = ({
	milestone,
	onPayment,
	loading = false,
	availableBalance = 0,
	checkingBalance = false,
	useExistingFunds = false,
	setUseExistingFunds,
	categoryFormSetting = null
}) => {
	const { t } = useTranslation();
	const [selectedPaymentMethodId, setSelectedPaymentMethodId] = useState<string | null>(null);
	const [isNewCard, setIsNewCard] = useState(false);
	const [isProcessing, setIsProcessing] = useState(false);

	// Calculate total amount needed
	const baseAmount = Number(milestone.amount) + Number((milestone as any).trackerAmount || 0) + Number((milestone as any).emergencyCost || 0);
	const platformFeeAmount = categoryFormSetting
		? getPlatformFeeForContract(baseAmount, categoryFormSetting)
		: baseAmount * PLATFORM_CONFIG.PLATFORM_FEE_PERCENTAGE;
	const platformFeePercentage = categoryFormSetting?.platformFee ?? (PLATFORM_CONFIG.PLATFORM_FEE_PERCENTAGE * 100);
	const platformFeePercentageDisplay = Number.isInteger(platformFeePercentage)
		? `${platformFeePercentage}%`
		: `${platformFeePercentage.toFixed(2)}%`;
	const totalAmountWithFee = baseAmount + platformFeeAmount;

	const handlePaymentMethodSelected = (paymentMethodId: string, isNew: boolean) => {
		setSelectedPaymentMethodId(paymentMethodId);
		setIsNewCard(isNew);
	};

	const handleSubmit = async (e: React.FormEvent) => {
		e.preventDefault();

		// If using existing funds, no need to validate payment method
		if (useExistingFunds) {
			setIsProcessing(true);
			try {
				const paymentData = {
					paymentMethodId: null,
					milestoneId: milestone.id,
					amount: milestone.amount,
					isNewCard: false,
					useExistingFunds: true,
				};

				console.log("Calling onPayment with wallet funds:", paymentData);
				onPayment(paymentData);
			} catch (err: any) {
				console.error("Payment form error:", err);
			} finally {
				setIsProcessing(false);
			}
			return;
		}

		// For card payment, validate payment method
		if (!selectedPaymentMethodId) {
			return;
		}

		setIsProcessing(true);

		try {
			const paymentData = {
				paymentMethodId: selectedPaymentMethodId,
				milestoneId: milestone.id,
				amount: milestone.amount,
				isNewCard: isNewCard,
				useExistingFunds: false,
			};

			console.log("Calling onPayment with data:", paymentData);
			onPayment(paymentData);
		} catch (err: any) {
			console.error("Payment form error:", err);
		} finally {
			setIsProcessing(false);
		}
	};

	return (
		<div className="space-y-4">
			{/* Available Balance Option */}
			{checkingBalance ? (
				<div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
					<div className="flex items-center gap-3">
						<Loader2 className="h-5 w-5 animate-spin text-blue-600" />
						<span className="text-blue-700">{t("enhancedPaymentForm.checkingBalance")}</span>
					</div>
				</div>
			) : availableBalance >= totalAmountWithFee ? (
				<div className="bg-green-50 border border-green-200 rounded-lg p-4">
					<div className="flex items-start gap-3">
						<div className="p-1 bg-green-100 rounded-lg">
							<Wallet className="h-5 w-5 text-green-600" />
						</div>
						<div className="flex-1">
							<h3 className="text-sm font-medium text-green-800 mb-2">
								{t("enhancedPaymentForm.sufficientFundsTitle")}
							</h3>
							<p className="text-sm text-green-700 mb-3">
								{t("enhancedPaymentForm.availableBalance")} <span className="font-semibold">{getDynamicCurrencySymbol()}{availableBalance.toFixed(2)}</span>
							</p>
							<div className="flex items-center gap-3">
								<label className="flex items-center gap-2 cursor-pointer">
									<input
										type="checkbox"
										checked={useExistingFunds}
										onChange={(e) => setUseExistingFunds?.(e.target.checked)}
										className="w-4 h-4 text-green-600 border-gray-300 rounded focus:ring-green-500"
									/>
									<span className="text-sm font-medium text-green-800">
										{t("enhancedPaymentForm.useWalletFunds")}
									</span>
								</label>
							</div>
							{useExistingFunds && (
								<p className="text-xs text-green-600 mt-2">
									{t("enhancedPaymentForm.walletBenefits")}
								</p>
							)}
						</div>
					</div>
				</div>
			) : availableBalance > 0 ? (
				<div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
					<div className="flex items-start gap-3">
						<div className="p-1 bg-yellow-100 rounded-lg">
							<Wallet className="h-5 w-5 text-yellow-600" />
						</div>
						<div className="flex-1">
							<h3 className="text-sm font-medium text-yellow-800 mb-2">
								{t("enhancedPaymentForm.partialFundsTitle")}
							</h3>
							<p className="text-sm text-yellow-700">
								{t("enhancedPaymentForm.availableBalance")} <span className="font-semibold">{getDynamicCurrencySymbol()}{availableBalance.toFixed(2)}</span>
								<br />
								{t("enhancedPaymentForm.partialFundsMessage", { amount: `${getDynamicCurrencySymbol()}${(totalAmountWithFee - availableBalance).toFixed(2)}` })}
							</p>
						</div>
					</div>
				</div>
			) : null}

			{/* Payment Method Selector - hide if using existing funds */}
			{!useExistingFunds && (
				<PaymentMethodSelector
					onPaymentMethodSelected={handlePaymentMethodSelected}
					loading={loading || isProcessing}
					selectedPaymentMethodId={selectedPaymentMethodId}
				/>
			)}

			{/* Payment Method Summary when using wallet funds */}
			{useExistingFunds && (
				<div className="bg-green-50 border border-green-200 rounded-lg p-4">
					<div className="flex items-center gap-3">
						<div className="p-2 bg-green-100 rounded-lg">
							<Wallet className="h-5 w-5 text-green-600" />
						</div>
						<div>
							<h3 className="text-sm font-medium text-green-800">{t("enhancedPaymentForm.paymentMethod")}</h3>
							<p className="text-sm text-green-700">{t("enhancedPaymentForm.usingWalletFunds")}</p>
						</div>
					</div>
				</div>
			)}

			{/* Payment Summary */}
			<Card className="p-6">
				<h4 className="font-medium text-gray-900 mb-3">{t("enhancedPaymentForm.paymentSummary")}</h4>
				<div className="space-y-2">
					<div className="flex justify-between text-sm">
						<span className="text-gray-600">{t("enhancedPaymentForm.milestoneAmount")}</span>
						<span className="font-medium">{getDynamicCurrencySymbol()}{Number(milestone.amount).toFixed(2)}</span>
					</div>
					{(Number((milestone as any).trackerAmount) > 0) && (
						<div className="flex justify-between text-sm">
							<span className="text-blue-600">{t("enhancedPaymentForm.smartWorkTracker")}</span>
							<span className="font-medium text-blue-600">{getDynamicCurrencySymbol()}{Number((milestone as any).trackerAmount).toFixed(2)}</span>
						</div>
					)}
					{(Number((milestone as any).emergencyCost) > 0) && (
						<div className="flex justify-between text-sm">
							<span className="text-red-600">{t("enhancedPaymentForm.emergencyCost")}</span>
							<span className="font-medium text-red-600">{getDynamicCurrencySymbol()}{Number((milestone as any).emergencyCost).toFixed(2)}</span>
						</div>
					)}
					<div className="flex justify-between text-sm">
						<span className="text-gray-600">{`${PLATFORM_CONFIG.PLATFORM_FEE_LABEL} (${platformFeePercentageDisplay}):`}</span>
						<span className="font-medium">{`${getDynamicCurrencySymbol()}${platformFeeAmount.toFixed(2)}`}</span>
					</div>
					{useExistingFunds && (
						<div className="flex justify-between text-sm">
							<span className="text-gray-600">{t("enhancedPaymentForm.paymentMethodLabel")}</span>
							<span className="font-medium text-green-600">{t("enhancedPaymentForm.walletFunds")}</span>
						</div>
					)}
					<div className="flex justify-between text-sm font-semibold text-lg border-t pt-2">
						<span>{t("enhancedPaymentForm.total")}</span>
						<span>{`${getDynamicCurrencySymbol()}${totalAmountWithFee.toFixed(2)}`}</span>
					</div>
					{useExistingFunds && (
						<div className="flex justify-between text-sm text-green-600 border-t pt-2">
							<span>{t("enhancedPaymentForm.remainingBalance")}</span>
							<span className="font-medium">{getDynamicCurrencySymbol()}{(availableBalance - totalAmountWithFee).toFixed(2)}</span>
						</div>
					)}
				</div>
			</Card>

			{/* Submit Button */}
			<form onSubmit={handleSubmit}>
				<Button
					type="submit"
					disabled={(!useExistingFunds && !selectedPaymentMethodId) || loading || isProcessing}
					className="w-full bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400"
				>
					{isProcessing ? (
						<>
							<Loader2 className="h-4 w-4 mr-2 animate-spin" />
							{useExistingFunds ? t("enhancedPaymentForm.processing") : t("enhancedPaymentForm.processingPayment")}
						</>
					) : (
						<>
							{useExistingFunds ? (
								<>
									<Wallet className="h-4 w-4 mr-2" />
									{`${t("enhancedPaymentForm.payWithWallet")} ${getDynamicCurrencySymbol()}${totalAmountWithFee.toFixed(2)}`}
								</>
							) : (
								<>
									<CreditCard className="h-4 w-4 mr-2" />
									{`${t("enhancedPaymentForm.pay")} ${getDynamicCurrencySymbol()}${totalAmountWithFee.toFixed(2)}`}
								</>
							)}
						</>
					)}
				</Button>
			</form>
		</div>
	);
};

// ✅ FIX: Wrapper component with Elements provider
const EnhancedPaymentForm: React.FC<Props> = (props) => {
	return (
		<Elements stripe={stripePromise}>
			<EnhancedPaymentFormContent {...props} />
		</Elements>
	);
};

export default EnhancedPaymentForm;